import { EventEmitter } from 'eventemitter3';
import { bind } from 'bind-event-listener';

import { CSM_WIDGET_POST_MESSAGE_NAMESPACE } from './consts';
import type { PostMessage } from './types';
import { isCSMWidgetMessage } from './utils';

type PostMessageBridgeOptions = {
	target: Window | null;
	origin: string;
};

type MessagePayload<Message extends PostMessage> =
	Extract<Message, { type: Message['type'] }> extends {
		payload: infer Payload;
	}
		? Payload
		: never;

export type EventCallback<Message extends PostMessage, Event extends Message['type']> =
	Extract<Message, { type: Event }> extends { payload: infer Payload }
		? (msg: Payload) => void
		: () => void;

export const createPostMessageBridge = <
	Messages extends {
		sendable: PostMessage;
		subscribable: PostMessage;
	},
>({
	target,
	origin,
}: PostMessageBridgeOptions): {
	send: <Event extends Messages['sendable']['type']>(
		event: Event,
		...args: MessagePayload<Extract<Messages['sendable'], { type: Event }>> extends never
			? []
			: [MessagePayload<Extract<Messages['sendable'], { type: Event }>>]
	) => void;
	on: <Event extends Messages['subscribable']['type']>(
		event: Event,
		cb: EventCallback<Messages['subscribable'], Event>,
	) => () => void;
	destroy: () => void;
} => {
	if (!target) {
		throw new Error('Target not available');
	}

	const emitter = new EventEmitter();

	const postMessageCallback = (event: MessageEvent) => {
		if (!isCSMWidgetMessage(event.data)) {
			return;
		}

		if (event.source && target !== event.source) {
			return;
		}

		const { namespace, ...message } = event.data;

		if ('payload' in message) {
			emitter.emit(message.type, message.payload);
		} else {
			emitter.emit(message.type);
		}
	};

	const unbind = bind(window, { type: 'message', listener: postMessageCallback });

	const send = <Event extends Messages['sendable']['type']>(
		event: Event,
		...args: MessagePayload<Extract<Messages['sendable'], { type: Event }>> extends never
			? []
			: [MessagePayload<Extract<Messages['sendable'], { type: Event }>>]
	): void => {
		const [payload] = args;

		target.postMessage(
			typeof payload !== 'undefined'
				? { type: event, namespace: CSM_WIDGET_POST_MESSAGE_NAMESPACE, payload }
				: { type: event, namespace: CSM_WIDGET_POST_MESSAGE_NAMESPACE },
			origin,
		);
	};

	const on = <Event extends Messages['subscribable']['type']>(
		event: Event,
		cb: EventCallback<Messages['subscribable'], Event>,
	) => {
		emitter.on(event, cb);

		return (): void => {
			emitter.off(event, cb);
		};
	};

	return {
		send,
		on,
		destroy: (): void => {
			unbind();
			emitter.removeAllListeners();
		},
	};
};
